/**
 * O formato de toda resposta do pacote: `{ ok: true, dados }` ou `{ ok: false, erro }`.
 *
 * POR QUE O ERRO CARREGA DUAS FRASES, e não uma, medido em `admin.html` em 19/09/2026: o legado
 * traduz a mensagem com `traduzirErro` em 5 lugares (`4808`, `5860`, `6433`, `7762`, `7799`) e, em
 * cerca de 90 outros, mostra a mensagem CRUA depois de um prefixo (`'Erro ao salvar: ' +
 * error.message`). Entregar só a traduzida obrigaria a tela nova a mostrar texto diferente do
 * legado nesses 90 lugares. Então o erro leva as duas: `frase` (já traduzida) e `mensagem` (a
 * original), e cada tela usa a que o legado usava naquele ponto.
 *
 * POR QUE O CORPO JÁ LIDO: numa Edge Function, o supabase-js devolve sempre a mensagem genérica
 * "non-2xx status code", e o motivo real vem no corpo da resposta (`error.context`). O corpo de uma
 * resposta só pode ser lido UMA vez; se a tela recebesse o `Response` e este pacote já o tivesse
 * lido, a segunda leitura falharia. Aqui ele é lido uma vez e entregue pronto.
 */

// A tradução pura das mensagens, com as frases exatas do legado.
import { traduzirErro } from './erros.js';

/** O corpo JSON de uma resposta de erro de Edge Function, com os dois campos que o legado lê. */
export interface CorpoDeErro {
  // O motivo em texto que a função escreveu (lido em admin.html:6328 e na mensagem do e-mail).
  readonly error?: unknown;
  // O código de regra de negócio (lido na mensagem de erro do e-mail de atendimento).
  readonly codigo?: unknown;
  // O resto do corpo, que o legado não lê, fica disponível sem tipo.
  readonly [chave: string]: unknown;
}

/** Tudo o que se sabe de uma falha, sem nada inventado: campo desconhecido é `null`. */
export interface ErroDeDados {
  // A frase de `traduzirErro(mensagem)`, para os pontos em que o legado traduz.
  readonly frase: string;
  // A mensagem original do Supabase, para os pontos em que o legado mostra `error.message`.
  readonly mensagem: string;
  // O código do PostgREST (ex.: "23505" de chave duplicada), quando a falha veio do banco.
  readonly codigo: string | null;
  // O status HTTP da resposta, quando houve resposta.
  readonly status: number | null;
  // O corpo de erro de Edge Function, já lido, quando veio em JSON.
  readonly corpo: CorpoDeErro | null;
}

/** A resposta de toda função do pacote. A tela decide pelo `ok`, e nunca por exceção. */
export type Resultado<T> = { readonly ok: true; readonly dados: T } | { readonly ok: false; readonly erro: ErroDeDados };

/** Monta o lado de sucesso. */
export function sucesso<T>(dados: T): Resultado<T> {
  return { ok: true, dados };
}

/** Monta o lado de falha a partir da mensagem original e do que mais se souber dela. */
export function falha(
  mensagem: string | null | undefined,
  extra: { codigo?: string | null; status?: number | null; corpo?: CorpoDeErro | null } = {},
): { readonly ok: false; readonly erro: ErroDeDados } {
  // Mensagem ausente vira texto vazio, e a tradução devolve a frase genérica, como no legado.
  const original = mensagem ?? '';
  return {
    ok: false,
    erro: {
      frase: traduzirErro(original),
      mensagem: original,
      codigo: extra.codigo ?? null,
      status: extra.status ?? null,
      corpo: extra.corpo ?? null,
    },
  };
}

/** A mensagem de qualquer coisa lançada, sem supor que é um `Error`. */
export function mensagemDe(lancado: unknown): string {
  // `Error` e parentes: a mensagem dele.
  if (lancado instanceof Error) return lancado.message;
  // Qualquer outra coisa: o texto dela, para não perder a informação.
  return String(lancado);
}

/** O formato que o PostgREST e o Storage devolvem: `{ data, error }`, com `status` no PostgREST. */
interface RespostaBruta {
  readonly data: unknown;
  readonly error: { readonly message: string; readonly code?: string; readonly status?: number } | null;
  readonly status?: number;
}

/**
 * Executa uma consulta do banco ou do Storage e a converte em `Resultado`.
 *
 * O `data` é entregue como veio, com o tipo que a função chamadora declara, porque o cliente do
 * Supabase deste pacote não carrega o esquema do banco (ver `cliente.ts`): o tipo de cada linha
 * vem de `tipos.ts`, derivado do legado, e é a função de cada módulo que o afirma.
 *
 * Exceção lançada (rede caída antes da resposta, por exemplo) vira falha, e não sobe: a tela
 * decide pelo `ok`, e uma exceção solta faria a tela travar em "Carregando" sem aviso nenhum.
 */
export async function executar<T>(consulta: PromiseLike<RespostaBruta>): Promise<Resultado<T>> {
  try {
    // A consulta do supabase-js só vai à rede quando é aguardada.
    const resposta = await consulta;
    // Falha do banco ou do Storage: a mensagem original, o código e o status que houver.
    if (resposta.error) {
      return falha(resposta.error.message, {
        codigo: resposta.error.code ?? null,
        status: resposta.status ?? resposta.error.status ?? null,
      });
    }
    // Sucesso: o dado com o tipo afirmado pelo módulo que conhece a consulta.
    return sucesso(resposta.data as T);
  } catch (lancado) {
    // Nada respondeu: a mensagem do que foi lançado, que costuma ser a de rede.
    return falha(mensagemDe(lancado));
  }
}

/** O erro que `functions.invoke` devolve: a mensagem e o `context`, que costuma ser o `Response`. */
interface ErroDeInvocacao {
  readonly message: string;
  readonly context?: unknown;
}

/**
 * Lê o status e o corpo JSON do `context` de um erro de Edge Function, sem nunca lançar.
 *
 * O `context` é o `Response` quando a função respondeu com status fora de 2xx, e é outra coisa
 * (o erro de rede) quando nada respondeu. Corpo que não é JSON (o 404 do gateway, por exemplo)
 * fica `null`, como o `catch` vazio do legado (`admin.html:6330`).
 */
export async function lerErroDeFuncao(erro: ErroDeInvocacao): Promise<{ readonly ok: false; readonly erro: ErroDeDados }> {
  // O contexto, quando é um Response, tem `status` numérico e `json()`.
  const contexto = erro.context as { status?: unknown; json?: () => Promise<unknown> } | undefined;
  // Status só quando é número de verdade.
  const status = typeof contexto?.status === 'number' ? contexto.status : null;
  // O corpo lido uma vez, aqui, porque um Response não aceita segunda leitura.
  let corpo: CorpoDeErro | null = null;
  try {
    // Sem `json`, o contexto não é resposta HTTP, e não há corpo a ler.
    if (typeof contexto?.json === 'function') {
      const lido = await contexto.json();
      // Só objeto conta como corpo; número ou texto solto não têm `error` nem `codigo`.
      if (lido && typeof lido === 'object') corpo = lido as CorpoDeErro;
    }
  } catch (_ignorado) {
    // Corpo que não veio em JSON: fica sem corpo, e vale a mensagem genérica.
  }
  return falha(erro.message, { status, corpo });
}
